import './Datepicker.css';

import React from 'react';
import classnames from 'classnames';
import { Icon } from '../../Icon/Icon';
import { formatRangeOfDays, toFullDaysRange } from '../../../util/util';
import { t } from '../../../locales/translate';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { FormField } from '../../../model/form';
import { FormTextField } from '../TextField/FormTextField';
import { StyledDayPicker } from '../styled_day_picker/styled_day_picker';
import { DateRange, Matcher } from 'react-day-picker';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { Hourpicker } from './Hourpicker';

interface Props extends FormField<[Date | undefined, Date | undefined]> {
  position?: PopperJS.Placement;
  disabledDays?: Matcher | Matcher[];
  hourpicker?: boolean;
  maxWidth?: string | number;
}

export function FormRangepicker({
  name,
  value,
  label,
  required,
  disabled,
  description,
  className,
  position = 'bottom-start',
  disabledDays,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  touched,
  valid,
  focused,
  icon,
  errors,
  hourpicker,
  maxWidth,
}: Props): JSX.Element {
  const displayErrors = touched && !valid;

  function handleTimeChange(date: Date, part: 'start' | 'end'): void {
    const newRange: [Date | undefined, Date | undefined] = [value?.[0], value?.[1]];
    const idx = part === 'start' ? 0 : 1;
    if (value && value[idx]) {
      const updated = new Date(value[idx]);
      updated.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
      newRange[idx] = updated;
    }
    onChange?.(newRange);
  }

  function handleDateChange(dateRange: DateRange): void {
    if (!dateRange) return;
    if (!(value?.[0] && value?.[1])) {
      // If no hour value is set, set to 00-23:59:59
      const adjustedRange = toFullDaysRange([dateRange.from, dateRange.to]);
      onChange?.(adjustedRange);
      return;
    }

    const from = dateRange.from ? new Date(dateRange.from) : undefined;
    const to = dateRange.to ? new Date(dateRange.to) : undefined;
    if (from && value[0]) {
      from.setHours(value[0].getHours(), value[0].getMinutes(), value[0].getSeconds(), value[0].getMilliseconds());
    }
    if (to && value[1]) {
      to.setHours(value[1].getHours(), value[1].getMinutes(), value[1].getSeconds(), value[1].getMilliseconds());
    }
    onChange?.([from, to]);
  }

  return (
    <div className={classnames('datepicker datepicker--range-of-days', className)} style={{ maxWidth }}>
      <Tooltip
        position={position}
        mode="click"
        gap="none"
        arrow={false}
        rounded={false}
        open={focused}
        onChange={(open: boolean) => {
          (open ? onFocus : onBlur)?.();
        }}
        dontCloseOnParentClick
        reference={
          <div id={name} className="datepicker__input">
            <FormTextField
              displayOptional={!required}
              value={value && formatRangeOfDays([value[0], value[1]])}
              placeholder={placeholder}
              disabled={disabled}
              readOnly
              required={required}
              icon={icon}
              label={label}
              actions={
                (value?.filter(Boolean).length || 0) > 0 && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange?.();
                    }}
                  >
                    <Icon name="remove-circle--sm" />
                  </span>
                )
              }
            />
          </div>
        }
      >
        <StyledDayPicker
          selected={value ? { from: value[0], to: value[1] } : undefined}
          month={value?.[0] || new Date()}
          numberOfMonths={1}
          disabled={disabledDays}
          onSelect={handleDateChange}
          mode="range"
          modifiers={{ start: value?.[0], end: value?.[1] }}
        />
        {hourpicker && (
          <div className="datepicker__hourpicker">
            <Hourpicker
              label={t.component.picker.startTime}
              date={value?.[0]}
              onChange={(date) => handleTimeChange(date, 'start')}
            />
            <Hourpicker
              label={t.component.picker.endTime}
              date={value?.[1]}
              onChange={(date) => handleTimeChange(date, 'end')}
            />
          </div>
        )}
      </Tooltip>
      <div className="datepicker__description">
        {description && <div className="input__description">{description}</div>}
        <Collapse open={displayErrors}>
          {errors?.map((error) => {
            return (
              <div className="input__error-message" key={error}>
                {t.errors[error] || error}
              </div>
            );
          })}
        </Collapse>
      </div>
    </div>
  );
}
